import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { BRANDS_INITIAL, MANAGERS, SPECIAL_DAYS_INITIAL, BRAND_FOLLOWERS_INITIAL } from '../data/initial';

const now = new Date();
const DEFAULT_MONTH = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`;

// ── seed helpers ──────────────────────────────────────────────────────────────
async function seedBrandsIfEmpty() {
  const { data } = await supabase.from('brands').select('id').limit(1);
  if (data && data.length > 0) return;
  await supabase.from('brands').insert(
    BRANDS_INITIAL.map(b => ({
      id: b.id, name: b.name,
      manager_id: b.managerId,
      target_posts: b.targetPosts,
      target_videos: b.targetVideos,
    }))
  );
}

async function seedSpecialDaysIfEmpty() {
  const { data } = await supabase.from('special_days').select('id').limit(1);
  if (data && data.length > 0) return;
  await supabase.from('special_days').insert(
    SPECIAL_DAYS_INITIAL.map(s => ({
      id: s.id, name: s.name, date: s.date, month: s.month
    }))
  );
}

async function seedFollowersIfEmpty() {
  const { data } = await supabase.from('followers').select('id').limit(1);
  if (data && data.length > 0) return;
  const rows = [];
  Object.entries(BRAND_FOLLOWERS_INITIAL).forEach(([month, platforms]) => {
    Object.entries(platforms).forEach(([platform, brands]) => {
      Object.entries(brands).forEach(([brandName, count]) => {
        rows.push({
          id: `${brandName}_${month}_${platform}`.replace(/\s+/g,'_'),
          brand_name: brandName, month, platform, count
        });
      });
    });
  });
  // insert in chunks of 100
  for (let i = 0; i < rows.length; i += 100) {
    await supabase.from('followers').insert(rows.slice(i, i+100));
  }
}

// ── main hook ─────────────────────────────────────────────────────────────────
export function useStore() {
  const [currentMonth, setCurrentMonthState] = useState(DEFAULT_MONTH);
  const [brands, setBrands]           = useState([]);
  const [monthlyRecords, setMonthlyRecords] = useState({});
  const [tasks, setTasks]             = useState({});       // key: brandId_month
  const [followers, setFollowers]     = useState({});       // key: brandName_month_platform
  const [specialDays, setSpecialDays] = useState([]);
  const [loading, setLoading]         = useState(true);

  // ── initial load + seed ──────────────────────────────────────────────────────
  useEffect(() => {
    async function init() {
      await seedBrandsIfEmpty();
      await seedSpecialDaysIfEmpty();
      await seedFollowersIfEmpty();
      await Promise.all([
        loadBrands(), loadSpecialDays(), loadFollowers()
      ]);
      setLoading(false);
    }
    init();
  }, []);

  // reload records/tasks when month changes
  useEffect(() => {
    if (!loading) {
      loadRecordsForMonth(currentMonth);
      loadTasksForMonth(currentMonth);
    }
  }, [currentMonth, loading]);

  // ── realtime subscriptions ────────────────────────────────────────────────────
  useEffect(() => {
    const brandsSub = supabase.channel('brands-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'brands' }, () => loadBrands())
      .subscribe();

    const recordsSub = supabase.channel('records-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'monthly_records' }, () => loadRecordsForMonth(currentMonth))
      .subscribe();

    const tasksSub = supabase.channel('tasks-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks' }, () => loadTasksForMonth(currentMonth))
      .subscribe();

    const followersSub = supabase.channel('followers-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'followers' }, () => loadFollowers())
      .subscribe();

    const sdSub = supabase.channel('sd-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'special_days' }, () => loadSpecialDays())
      .subscribe();

    return () => {
      supabase.removeChannel(brandsSub);
      supabase.removeChannel(recordsSub);
      supabase.removeChannel(tasksSub);
      supabase.removeChannel(followersSub);
      supabase.removeChannel(sdSub);
    };
  }, [currentMonth]);

  // ── loaders ───────────────────────────────────────────────────────────────────
  async function loadBrands() {
    const { data } = await supabase.from('brands').select('*').order('name');
    if (data) setBrands(data.map(b => ({
      id: b.id, name: b.name,
      managerId: b.manager_id,
      targetPosts: b.target_posts,
      targetVideos: b.target_videos,
    })));
  }

  async function loadRecordsForMonth(month) {
    const { data } = await supabase.from('monthly_records').select('*').eq('month', month);
    if (!data) return;
    const map = {};
    data.forEach(r => {
      map[`${r.brand_id}_${r.month}`] = {
        brandId: r.brand_id, month: r.month,
        contentPlanToDesigner: r.content_plan_to_designer,
        contentPlanToClient:   r.content_plan_to_client,
        monthlyReportSent:     r.monthly_report_sent,
        weeklyUpdates:         r.weekly_updates || { w1:false,w2:false,w3:false,w4:false },
        deliverables:          r.deliverables   || { w1Posts:0,w2Posts:0,w3Posts:0,w4Posts:0,w1Videos:0,w2Videos:0,w3Videos:0,w4Videos:0 },
        specialDaysDone:       r.special_days_done || {},
      };
    });
    setMonthlyRecords(prev => ({ ...prev, ...map }));
  }

  async function loadTasksForMonth(month) {
    const { data } = await supabase.from('tasks').select('*').eq('month', month).order('created_at');
    if (!data) return;
    const map = {};
    data.forEach(t => {
      const key = `${t.brand_id}_${t.month}`;
      if (!map[key]) map[key] = [];
      map[key].push({ id: t.id, text: t.text, status: t.status, createdAt: t.created_at });
    });
    setTasks(prev => ({ ...prev, ...map }));
  }

  async function loadFollowers() {
    const { data } = await supabase.from('followers').select('*');
    if (!data) return;
    const map = {};
    data.forEach(f => {
      map[`${f.brand_name}_${f.month}_${f.platform}`] = f.count;
    });
    setFollowers(map);
  }

  async function loadSpecialDays() {
    const { data } = await supabase.from('special_days').select('*').order('date');
    if (data) setSpecialDays(data.map(s => ({ id: s.id, name: s.name, date: s.date, month: s.month })));
  }

  // ── public API ────────────────────────────────────────────────────────────────
  const getMonthRecord = useCallback((brandId, month) => {
    const key = `${brandId}_${month}`;
    return monthlyRecords[key] || {
      brandId, month,
      contentPlanToDesigner: false,
      contentPlanToClient:   false,
      monthlyReportSent:     false,
      weeklyUpdates:  { w1:false, w2:false, w3:false, w4:false },
      deliverables:   { w1Posts:0,w2Posts:0,w3Posts:0,w4Posts:0,w1Videos:0,w2Videos:0,w3Videos:0,w4Videos:0 },
      specialDaysDone: {},
      tasks: [],
    };
  }, [monthlyRecords]);

  const getTasksForBrand = useCallback((brandId, month) => {
    return tasks[`${brandId}_${month}`] || [];
  }, [tasks]);

  const getFollowerCount = useCallback((brandName, month, platform) => {
    return followers[`${brandName}_${month}_${platform}`] || 0;
  }, [followers]);

  // upsert monthly record field(s)
  const setMonthRecord = useCallback(async (brandId, month, patch) => {
    const existing = monthlyRecords[`${brandId}_${month}`];
    const merged = { ...getMonthRecord(brandId, month), ...patch };
    const row = {
      id:                       `${brandId}_${month}`,
      brand_id:                 brandId,
      month,
      content_plan_to_designer: merged.contentPlanToDesigner,
      content_plan_to_client:   merged.contentPlanToClient,
      monthly_report_sent:      merged.monthlyReportSent,
      weekly_updates:           merged.weeklyUpdates,
      deliverables:             merged.deliverables,
      special_days_done:        merged.specialDaysDone,
      updated_at:               new Date().toISOString(),
    };
    // optimistic update
    setMonthlyRecords(prev => ({ ...prev, [`${brandId}_${month}`]: merged }));
    await supabase.from('monthly_records').upsert(row, { onConflict: 'brand_id,month' });
  }, [monthlyRecords, getMonthRecord]);

  const addTask = useCallback(async (brandId, month, text) => {
    const id = Date.now().toString();
    const row = { id, brand_id: brandId, month, text, status: 'pending' };
    // optimistic
    const key = `${brandId}_${month}`;
    setTasks(prev => ({ ...prev, [key]: [...(prev[key]||[]), { id, text, status:'pending', createdAt: new Date().toISOString() }] }));
    await supabase.from('tasks').insert(row);
  }, []);

  const updateTask = useCallback(async (brandId, month, taskId, patch) => {
    const key = `${brandId}_${month}`;
    setTasks(prev => ({ ...prev, [key]: (prev[key]||[]).map(t => t.id===taskId ? {...t,...patch} : t) }));
    await supabase.from('tasks').update(patch).eq('id', taskId);
  }, []);

  const deleteTask = useCallback(async (brandId, month, taskId) => {
    const key = `${brandId}_${month}`;
    setTasks(prev => ({ ...prev, [key]: (prev[key]||[]).filter(t => t.id!==taskId) }));
    await supabase.from('tasks').delete().eq('id', taskId);
  }, []);

  const setFollowerCount = useCallback(async (brandName, month, platform, count) => {
    const id = `${brandName}_${month}_${platform}`.replace(/\s+/g,'_');
    setFollowers(prev => ({ ...prev, [`${brandName}_${month}_${platform}`]: count }));
    await supabase.from('followers').upsert(
      { id, brand_name: brandName, month, platform, count, updated_at: new Date().toISOString() },
      { onConflict: 'brand_name,month,platform' }
    );
  }, []);

  const addSpecialDay = useCallback(async (name, date, month) => {
    const id = Date.now().toString();
    setSpecialDays(prev => [...prev, { id, name, date, month }]);
    await supabase.from('special_days').insert({ id, name, date, month });
  }, []);

  const deleteSpecialDay = useCallback(async (id) => {
    setSpecialDays(prev => prev.filter(s => s.id !== id));
    await supabase.from('special_days').delete().eq('id', id);
  }, []);

  const addBrand = useCallback(async (brand) => {
    const id = Date.now().toString();
    const newBrand = { ...brand, id };
    setBrands(prev => [...prev, newBrand]);
    await supabase.from('brands').insert({
      id, name: brand.name,
      manager_id:    brand.managerId,
      target_posts:  brand.targetPosts,
      target_videos: brand.targetVideos,
    });
  }, []);

  const deleteBrand = useCallback(async (id) => {
    setBrands(prev => prev.filter(b => b.id !== id));
    await supabase.from('brands').delete().eq('id', id);
  }, []);

  const setCurrentMonth = useCallback((month) => {
    setCurrentMonthState(month);
  }, []);

  return {
    state: {
      brands,
      managers: MANAGERS,
      specialDays,
      currentMonth,
      loading,
    },
    getMonthRecord: (brandId, month) => ({
      ...getMonthRecord(brandId, month),
      tasks: getTasksForBrand(brandId, month),
    }),
    getFollowerCount,
    setMonthRecord,
    addTask,
    updateTask,
    deleteTask,
    setFollowerCount,
    addSpecialDay,
    deleteSpecialDay,
    addBrand,
    deleteBrand,
    setCurrentMonth,
    // keep old setBrandFollower name working
    setBrandFollower: (month, platform, brandName, val) => setFollowerCount(brandName, month, platform, val),
  };
}
